/**
 * Query options for board-row comments.
 *
 * - `trackerCommentsListQueryOptions` — one row's flat thread (oldest first),
 *   for the open comments panel.
 * - `trackerCommentSummaryQueryOptions` — ONE query per board with every
 *   commented row's count + newest-comment time, shared by all the row badges
 *   (same query key → a single request, no N+1 request storm).
 *
 * Both go through the generated `TrackersService` (baseURL + auth applied).
 */
import { TrackersService } from '@inference.engine/workspace-api-client/web';
import { queryOptions } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

import type { ListTrackerCommentsResponseDto, TrackerCommentSummary } from '../model/types';
import { TRACKER_COMMENT_QUERY_KEYS } from './query-keys';

export interface TrackerCommentsListParams {
  trackerId: string;
  rowKey: string;
}

export const trackerCommentsListQueryOptions = (params: TrackerCommentsListParams) =>
  queryOptions({
    queryKey: TRACKER_COMMENT_QUERY_KEYS.list(params.trackerId, params.rowKey),
    queryFn: async ({ signal }): Promise<ListTrackerCommentsResponseDto> => {
      const response = await TrackersService.listComments({
        client: workspaceHttp,
        path: { trackerId: params.trackerId },
        query: { rowKey: params.rowKey },
        signal,
        // Suppresses the global error toast for every status, not only 404: the
        // panel shows its own state for each failure, so a toast would repeat it.
        // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });
      return response.data ?? { items: [], total: 0 };
    },
    enabled: Boolean(params.trackerId && params.rowKey),
  });

export const trackerCommentSummaryQueryOptions = (trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_COMMENT_QUERY_KEYS.summary(trackerId),
    queryFn: async ({ signal }): Promise<TrackerCommentSummary> => {
      const response = await TrackersService.listCommentCounts({
        client: workspaceHttp,
        path: { trackerId },
        signal,
      });
      const summary: TrackerCommentSummary = {};
      for (const row of response.data?.items ?? []) {
        summary[row.rowKey] = { count: row.count, latestAt: row.latestAt };
      }
      return summary;
    },
    enabled: Boolean(trackerId),
  });
